/**
 * HomeHero — Realty237 redesign (Feb 2026).
 *
 * Immersive hero with full-bleed tropical villa background, dark gradient overlay,
 * large display headline, and floating glassmorphism search bar.
 *
 * Inspired by Anthropic/Earthy direction validated by design_agent_full_stack.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

/**
 * Build a responsive Unsplash srcset from any Unsplash URL, dropping the
 * huge `w=2000&q=85` legacy variant. WebP output halves the payload vs
 * JPEG. `q=70` remains visually indistinguishable for a hero photo at
 * this size. If the URL is not Unsplash (e.g. a Cloudinary property
 * image), fall back to the raw URL so we don't break external hosts.
 */
const buildHeroSrcset = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  if (!rawUrl.includes('images.unsplash.com')) return null;
  // Strip existing w/q params to normalize.
  const base = rawUrl.split('?')[0];
  const widths = [800, 1200, 1600];
  return {
    src: `${base}?w=1200&q=55&fm=webp`,
    srcSet: widths
      .map((w) => `${base}?w=${w}&q=55&fm=webp ${w}w`)
      .join(', '),
    sizes: '100vw',
  };
};

// Local CSS gradient fallback — replaces the 2.2 MiB emergentagent.com PNG
// that used to ship on every cold visit while the hero-slides API was in
// flight. Zero-byte on the wire, matches the brand palette, prevents the
// hero from ever showing a black void during the first RTT.
const FALLBACK_GRADIENT =
  'linear-gradient(135deg, #1c1917 0%, #292524 35%, #7c2d12 75%, #ea580c 100%)';

const FALLBACK_SLIDE = {
  id: 'fallback',
  type: 'city',
  image: null,  // no image on wire — the gradient fills the frame instead
  title: 'L\'immobilier camerounais, simple et fiable',
  subtitle: 'Achetez, louez, investissez à Douala, Yaoundé et partout au Cameroun.',
  cta_label: null,
  cta_url: null,
};

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const HomeHero = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [purpose, setPurpose] = useState('sale'); // 'sale' | 'rent'
  const [city, setCity] = useState('');
  const [query, setQuery] = useState('');
  // DOM refs — read at submit time to defeat any state batching race condition
  // (typing fast then clicking Rechercher immediately could otherwise read
  // a stale React state closure).
  const cityRef = useRef(null);
  const queryRef = useRef(null);
  const purposeRef = useRef('sale');
  useEffect(() => { purposeRef.current = purpose; }, [purpose]);
  const [slides, setSlides] = useState([FALLBACK_SLIDE]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [interval, setIntervalMs] = useState(6000);
  const [paused, setPaused] = useState(false);
  // Track which slide images fail to load — we swap them for a brand gradient
  // so the hero never shows a black void (e.g. Unsplash 404, rate-limit).
  const [brokenIds, setBrokenIds] = useState(() => new Set());

  // D2 — Hero social-proof stats. Honest reflection of live DB data:
  //   - shows the exact count when below 50 (no misleading "+")
  //   - rounds down to the nearest 50/100/1000 with a "+" suffix once
  //     the count is statistically significant (≥ 50)
  //   - hides the strip entirely if the API fails OR if every value is 0,
  //     so we NEVER display hardcoded fake numbers.
  //
  // UX-001 hardening (Feb 2026 audit) : for a national-scale real
  // estate portal, showing weak numeric proof like "8 biens" / "2
  // villes" reads as a *negative* trust signal. We now gate the
  // NUMERIC strip behind a minimum-scale threshold and fall back to
  // qualitative badges ("Annonces vérifiées", "Réponse en 24h",
  // "Zéro frais d'inscription") until the platform reaches critical
  // mass (≥ 50 listings AND ≥ 5 cities).
  //
  // State shape:
  //   { ready: false }                          → don't render yet
  //   { ready: true, hide: true }               → API failed / no data → hide strip
  //   { ready: true, hide: false, mode: 'num' } → render numeric items
  //   { ready: true, hide: false, mode: 'qual'} → render qualitative badges
  const [heroStats, setHeroStats] = useState({ ready: false });

  useEffect(() => {
    let alive = true;

    // Qualitative fallback (UX-001) — always safe to show, no metrics
    // needed. Same visual slot as numeric strip.
    const qualitativeItems = [
      { value: '✓', label: 'Annonces vérifiées' },
      { value: '⚡', label: 'Réponse 24 h' },
      { value: '🎁', label: 'Inscription gratuite' },
    ];

    const NUMERIC_THRESHOLD = { listings: 50, cities: 5 };

    const fmtCount = (n) => {
      const v = Number(n);
      if (!Number.isFinite(v) || v <= 0) return null;
      if (v < 100) return '50+';
      if (v < 1000) return `${Math.floor(v / 100) * 100}+`; // "200+"
      return `${(Math.floor(v / 100) / 10).toFixed(1)}k+`;  // "1.2k+"
    };

    const fmtSmall = (n) => {
      // Villes : valeur exacte pour un chiffre déjà supérieur au seuil.
      const v = Number(n);
      if (!Number.isFinite(v) || v <= 0) return null;
      return String(Math.floor(v));
    };

    axios
      .get(`${API_URL}/home/stats`)
      .then((r) => {
        if (!alive) return;
        const d = r.data || {};
        const listings = Number(d.listings) || 0;
        const cities = Number(d.cities) || 0;

        // Threshold gate — below this, numbers are counter-productive.
        if (listings < NUMERIC_THRESHOLD.listings || cities < NUMERIC_THRESHOLD.cities) {
          setHeroStats({ ready: true, hide: false, mode: 'qual', items: qualitativeItems });
          return;
        }

        const items = [
          { value: fmtCount(listings),   label: 'biens vérifiés' },
          { value: fmtCount(d.agents),   label: 'agents partenaires' },
          { value: fmtSmall(cities),     label: 'villes couvertes' },
        ].filter((it) => it.value !== null);

        // Only show the strip if at least 2/3 stats are positive — a single
        // city + zero agents would look thin and unimpressive.
        if (items.length < 2) {
          setHeroStats({ ready: true, hide: false, mode: 'qual', items: qualitativeItems });
        } else {
          setHeroStats({ ready: true, hide: false, mode: 'num', items });
        }
      })
      .catch(() => {
        if (!alive) return;
        // Fallback : même en cas d'erreur, on affiche les badges
        // qualitatifs plutôt que rien — signal de confiance minimum.
        setHeroStats({ ready: true, hide: false, mode: 'qual', items: qualitativeItems });
      });

    return () => { alive = false; };
  }, []);

  // Preload each slide image to detect broken URLs (marks them so the
  // fallback gradient shows). We use the same responsive src as the
  // rendered <img> so the browser hits its cache when the real element
  // mounts — no double download.
  useEffect(() => {
    let alive = true;
    slides.forEach((s) => {
      if (!s?.image || brokenIds.has(s.id)) return;
      const srcset = buildHeroSrcset(s.image);
      const url = srcset ? srcset.src : s.image;
      const img = new Image();
      img.onerror = () => {
        if (!alive) return;
        setBrokenIds((prev) => {
          if (prev.has(s.id)) return prev;
          const next = new Set(prev);
          next.add(s.id);
          return next;
        });
      };
      img.src = url;
    });
    return () => {
      alive = false;
    };
  }, [slides, brokenIds]);

  // Fetch dynamic slides (city showcase + boosted properties)
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/home/hero-slides`)
      .then((r) => {
        const data = r.data || {};
        if (alive && Array.isArray(data.slides) && data.slides.length) {
          setSlides(data.slides);
          if (typeof data.rotation_interval_ms === 'number') setIntervalMs(data.rotation_interval_ms);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Auto-rotate slides (paused on hover, respects prefers-reduced-motion)
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;
    if (paused) return undefined;
    const id = window.setInterval(() => {
      setActiveIdx((i) => (i + 1) % slides.length);
    }, interval);
    return () => window.clearInterval(id);
  }, [slides.length, interval, paused]);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    // Read live DOM values (defeats React state batching race conditions).
    // Fall back to state if a ref hasn't mounted (SSR-safe).
    const liveCity = (cityRef.current?.value ?? city) || '';
    const liveQuery = (queryRef.current?.value ?? query) || '';
    const livePurpose = purposeRef.current ?? purpose;

    const trimmedCity = liveCity.trim();
    const trimmedQuery = liveQuery.trim();
    const params = new URLSearchParams();
    if (trimmedCity) params.set('city', trimmedCity);
    if (trimmedQuery) params.set('search', trimmedQuery);

    let basePath = '/properties';
    if (livePurpose === 'sale' || livePurpose === 'rent') {
      params.set('purpose', livePurpose);
    } else if (livePurpose === 'land') {
      params.set('type', 'land');
    } else if (livePurpose === 'developments') {
      basePath = '/developments';
    } else if (livePurpose === 'agents') {
      basePath = '/agents';
    }

    const qs = params.toString();
    navigate(qs ? `${basePath}?${qs}` : basePath);
  };

  const SEARCH_TABS = [
    { key: 'sale', label: t('nav.buy'), testid: 'hero-search-buy-tab' },
    { key: 'rent', label: t('nav.rent'), testid: 'hero-search-rent-tab' },
    { key: 'land', label: t('nav.land'), testid: 'hero-search-land-tab' },
    { key: 'developments', label: t('nav.developments'), testid: 'hero-search-developments-tab' },
    { key: 'agents', label: t('nav.agentsPro'), testid: 'hero-search-agents-tab' },
  ];

  return (
    <>
    <section
      className="relative overflow-hidden min-h-[520px] sm:min-h-[560px] lg:min-h-[600px]"
      data-testid="home-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Stacked slides with crossfade + Ken Burns zoom */}
      <div className="absolute inset-0 -z-0 overflow-hidden bg-stone-900" data-testid="home-hero-carousel">
        {slides.map((slide, idx) => {
          const active = idx === activeIdx;
          const broken = brokenIds.has(slide.id);
          const srcset = broken ? null : buildHeroSrcset(slide.image);
          // Non-Unsplash URLs pass through (Cloudinary boosted-property
          // photos, etc.) — still LCP-discoverable via <img>.
          const rawSrc = broken || !slide.image ? null : (srcset ? srcset.src : slide.image);
          const isLcpCandidate = idx === 0;  // first slide = LCP element
          return (
            <div
              key={slide.id}
              className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
              style={{
                opacity: active ? 1 : 0,
                background: rawSrc ? undefined : FALLBACK_GRADIENT,
              }}
              data-testid={`home-hero-slide-${idx}`}
              aria-hidden={!active}
            >
              {rawSrc && (
                <img
                  src={rawSrc}
                  srcSet={srcset ? srcset.srcSet : undefined}
                  sizes={srcset ? srcset.sizes : undefined}
                  alt={slide.title || 'Realty237'}
                  width="1600"
                  height="900"
                  loading={isLcpCandidate ? 'eager' : 'lazy'}
                  fetchPriority={isLcpCandidate ? 'high' : 'auto'}
                  decoding="async"
                  className="w-full h-full object-cover will-change-transform"
                  style={{
                    animation: active
                      ? `kenBurns ${Math.max(interval, 6000) + 2000}ms ease-in-out forwards`
                      : 'none',
                  }}
                  onError={() => {
                    setBrokenIds((prev) => {
                      if (prev.has(slide.id)) return prev;
                      const next = new Set(prev);
                      next.add(slide.id);
                      return next;
                    });
                  }}
                />
              )}
            </div>
          );
        })}
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/25 via-stone-900/35 to-stone-900/55 pointer-events-none" />

        {/* Slide dots (only if >1) */}
        {slides.length > 1 && (
          <div
            className="absolute bottom-6 right-6 flex gap-2 z-20"
            data-testid="home-hero-dots"
          >
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Aller à la slide ${i + 1}`}
                onClick={() => setActiveIdx(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === activeIdx ? 'w-8 bg-brand-400' : 'w-2 bg-white/60 hover:bg-white'
                }`}
                data-testid={`home-hero-dot-${i}`}
              />
            ))}
          </div>
        )}

        {/* Slide caption — bottom-RIGHT (matches reference image
            "Douala, la capitale économique / Explorer Douala →").
            Shown only on xl+ (≥ 1280px) where the hero has enough
            horizontal room. At md (768) and lg (1024) it used to
            overlap the description + social-proof strip, hurting
            legibility on iPad portrait / landscape. */}
        {slides[activeIdx] && (
          <div
            key={slides[activeIdx].id + '-caption'}
            className="hidden xl:block absolute right-10 bottom-14 z-20 max-w-sm text-right"
            data-testid="home-hero-caption"
          >
            {slides[activeIdx].badge && (
              <span className="inline-block text-label font-bold uppercase tracking-widest text-stone-900 bg-amber-400 px-2 py-0.5 rounded-md mb-2 animate-fade-in-up">
                {slides[activeIdx].badge}
              </span>
            )}
            {slides[activeIdx].cta_url ? (
              <Link
                to={slides[activeIdx].cta_url}
                className="group inline-flex flex-col items-end"
                data-testid="home-hero-caption-link"
              >
                <p className="font-display text-white font-bold text-lg sm:text-xl leading-tight mb-1 drop-shadow-lg group-hover:text-brand-300 transition-colors">
                  {slides[activeIdx].title}
                </p>
                {slides[activeIdx].subtitle && (
                  <p className="text-stone-100/85 text-xs sm:text-sm leading-snug drop-shadow-md">
                    {slides[activeIdx].subtitle}
                  </p>
                )}
                {slides[activeIdx].cta_label && (
                  <span className="mt-1.5 inline-flex items-center gap-1 text-brand-300 text-xs font-semibold group-hover:gap-2 transition-all">
                    {slides[activeIdx].cta_label}
                    <ArrowRight className="h-3 w-3" />
                  </span>
                )}
              </Link>
            ) : (
              <>
                <p className="font-display text-white font-bold text-lg sm:text-xl leading-tight mb-1 drop-shadow-lg">
                  {slides[activeIdx].title}
                </p>
                {slides[activeIdx].subtitle && (
                  <p className="text-stone-100/85 text-xs sm:text-sm leading-snug drop-shadow-md">
                    {slides[activeIdx].subtitle}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Content overlay — immersive hero (reference layout Feb 2026).
          Large vertical padding so the hero image dominates the fold ;
          the search form is a *separate* floating card in a sibling
          section below (overlaps via negative margin) so it never
          hides the H1 / caption. */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-10 2xl:py-20 flex flex-col">
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-label sm:text-xs font-medium tracking-wider uppercase mb-4 animate-fade-in-up">
            🇨🇲 La référence immobilière au Cameroun
          </p>
          <h1
            className="font-hero text-white text-display font-bold animate-fade-in-up"
            data-testid="hero-h1"
          >
            L&apos;immobilier camerounais,<br />
            <span className="text-brand-400">simple</span> et{' '}
            <span className="text-brand-400">fiable</span>.
          </h1>
          <p className="text-stone-100/90 text-base sm:text-lg mt-4 max-w-xl leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Maisons, appartements, terrains à Douala, Yaoundé, Bafoussam, Kribi et bien au-delà —
            achat, location ou investissement, en toute confiance.
          </p>

          {/* Social-proof strip — dual mode (UX-001) :
              - mode "num" : chiffres réels (KPIs plateforme mature)
              - mode "qual" : badges qualitatifs si sous les seuils
                (50 biens / 5 villes). Évite le signal négatif d'un
                "8 biens vérifiés" pour un portail national. */}
          {heroStats.ready && !heroStats.hide && heroStats.items?.length > 0 && (
            <div
              className="mt-6 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2 text-white/90 animate-fade-in-up"
              style={{ animationDelay: '0.15s' }}
              data-testid="hero-social-proof"
              data-mode={heroStats.mode}
            >
              {heroStats.items.map((s) => (
                <div key={s.label} className="flex flex-col items-center text-center sm:flex-row sm:items-baseline sm:gap-1.5">
                  <span
                    className={heroStats.mode === 'qual'
                      ? 'text-lg sm:text-2xl leading-none mb-0.5 sm:mb-0'
                      : 'text-xl sm:text-3xl font-extrabold text-brand-300 tabular-nums'}
                    data-testid={`hero-stat-${s.label.replace(/\s+/g, '-')}`}
                    aria-hidden={heroStats.mode === 'qual' ? 'true' : undefined}
                  >
                    {s.value}
                  </span>
                  <span className={heroStats.mode === 'qual'
                    ? 'text-label sm:text-sm font-semibold text-white leading-tight'
                    : 'text-label sm:text-sm text-white/80 leading-tight'}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>

    {/* Floating search card — sibling of hero, overlaps hero bottom via
        negative margin. On mobile the overlap is reduced to keep the
        social-proof badges (« Annonces vérifiées · Réponse 24h ·
        Inscription gratuite ») fully readable above the card. */}
    <div className="relative z-30 max-w-4xl mx-auto px-4 sm:px-6 -mt-4 sm:-mt-16">
      <form
        onSubmit={handleSearch}
        className="bg-white/95 backdrop-blur-2xl border border-white/60 shadow-2xl rounded-2xl p-4 sm:p-6 animate-fade-in-up"
        style={{ animationDelay: '0.2s' }}
        data-testid="hero-search-form"
      >
        {/* Purpose tabs — horizontally scrollable on mobile. A right-edge
            fade + subtle chevron acts as scroll affordance so users
            notice the last tabs (Développement, Agents Pro) exist even
            when they overflow. Both hidden once the scroll reaches the
            end via a plain-JS onScroll handler. */}
        <div className="relative mb-3 sm:mb-4">
          <div
            className="hero-tabs-scroll inline-flex bg-stone-100 rounded-xl p-1 overflow-x-auto max-w-full w-full"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            role="tablist"
            data-testid="hero-search-tabs"
            onScroll={(e) => {
              const el = e.currentTarget;
              const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
              el.dataset.atEnd = atEnd ? '1' : '0';
            }}
          >
            {SEARCH_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={purpose === tab.key}
                onClick={() => { purposeRef.current = tab.key; setPurpose(tab.key); }}
                className={`shrink-0 px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-colors whitespace-nowrap ${
                  purpose === tab.key
                    ? 'bg-white text-brand-600 shadow-sm'
                    : 'text-stone-500 hover:text-stone-900'
                }`}
                data-testid={tab.testid}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Right-edge scroll indicator (mobile only). Marked
              aria-hidden — purely decorative affordance. */}
          <div
            aria-hidden="true"
            className="hero-tabs-fade pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 pl-6 sm:hidden"
          >
            <ChevronRight className="h-4 w-4 text-stone-500 animate-pulse" />
          </div>
        </div>

        {/* Search row: Ville input + Quartier input + Rechercher button */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Ville (ex: Douala, Yaoundé…)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              ref={cityRef}
              className="h-12 pl-10 bg-white border-stone-200 rounded-xl"
              data-testid="hero-search-city"
            />
          </div>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Quartier, type, mots-clés…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              ref={queryRef}
              className="h-12 pl-10 bg-white border-stone-200 rounded-xl"
              data-testid="hero-search-query"
            />
          </div>
          <Button
            type="submit"
            className="h-12 px-6 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-semibold gap-2 shadow-md"
            data-testid="hero-search-btn"
          >
            {t('home.searchCta')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
    </>
  );
};

export default HomeHero;
